import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { fetchIssues, createSubscription, insertIssue } from '../services/dataService';
import { MessageSquare, Search, Plus, Save, ChevronDown, CheckCircle, Clock, XCircle } from 'lucide-react';
import { PageHeader } from '../components/layout';
import { Button, Input, Card, Badge, Modal, ModalFooter, Spinner, EmptyState } from '../components/ui';
import { cn } from '../utils/cn';

const QUESTION_CATEGORIES = ['Shopper', 'System', 'Retailer-Specific', 'Brand-Specific', 'General'];
const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';

function Clarifications() {
  const { user, isAuthenticated, isLoading: authLoading } = BYPASS_AUTH
    ? { user: { email: 'dev@example.com' }, isAuthenticated: true, isLoading: false }
    : useAuth();

  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);  // Start true to show spinner on initial load
  const [apiError, setApiError] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showAnsweredOnly, setShowAnsweredOnly] = useState(false);
  const [showNewQuestionModal, setShowNewQuestionModal] = useState(false);
  const [newQuestion, setNewQuestion] = useState({});
  const [isSavingQuestion, setIsSavingQuestion] = useState(false);
  const [questionError, setQuestionError] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState([]);

  useEffect(() => {
    // Wait for auth to settle before firing a request without a bearer
    if (authLoading) {
      return;
    }

    // Auth done but not authenticated - stop loading, don't fetch
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let cleanup = null;

    (async () => {
      setIsLoading(true);
      setApiError(false);

      try {
        const fetched = await fetchIssues(['clarifications']);

        if (fetched === null) {
          setApiError(true);
        } else {
          setQuestions(fetched);
          cleanup = createSubscription(
            'swissues',
            (newData) => setQuestions(newData),
            () => fetchIssues(['clarifications'])
          );
        }
      } catch (err) {
        console.error('[Clarifications] API load error:', err);
        setApiError(true);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      if (cleanup) cleanup();
    };
  }, [authLoading, isAuthenticated]);

  const filteredQuestions = useMemo(() => {
    let filtered = questions;

    if (activeTab === 'my') {
      filtered = filtered.filter(q => q.userEmail === user?.email);
    }

    if (showAnsweredOnly) {
      filtered = filtered.filter(q => q.resolutionStatus === 'Answered');
    }

    if (statusFilter.length > 0) {
      filtered = filtered.filter(q => statusFilter.includes(q.resolutionStatus || 'Open'));
    }

    if (searchInput.trim()) {
      const q = searchInput.toLowerCase();
      filtered = filtered.filter(item => {
        const created = item.createdAt ? new Date(item.createdAt).toLocaleString().toLowerCase() : '';
        const category = item.issueType || '';
        return (
          (item.comment || '').toLowerCase().includes(q) ||
          (item.resolutionNotes || '').toLowerCase().includes(q) ||
          (item.retailer || '').toLowerCase().includes(q) ||
          (item.level || '').toLowerCase().includes(q) ||
          (item.userEmail || '').toLowerCase().includes(q) ||
          category.toLowerCase().includes(q) ||
          created.includes(q)
        );
      });
    }

    return filtered.sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [questions, activeTab, showAnsweredOnly, statusFilter, searchInput, user]);

  const handleNewQuestionChange = (field, value) => {
    setNewQuestion(prev => ({ ...prev, [field]: value }));
  };

  const openNewQuestionModal = () => {
    setNewQuestion({
      text: '',
      retailer: '',
      brand: '',
      category: 'General'
    });
    setQuestionError(null);
    setIsSavingQuestion(false);
    setShowNewQuestionModal(true);
  };

  const saveNewQuestion = async () => {
    if (isSavingQuestion) return;

    if (!newQuestion.text || !newQuestion.text.trim()) {
      setQuestionError('Question text is required. Please enter your question.');
      return;
    }

    setIsSavingQuestion(true);
    setQuestionError(null);

    const payload = {
      source: 'clarifications',
      comment: newQuestion.text.trim(),
      resolution_status: 'Open',
      issue_type: newQuestion.category || 'General',
    };

    if (newQuestion.retailer && newQuestion.retailer.trim()) {
      payload.retailer = newQuestion.retailer.trim();
    }
    if (newQuestion.brand && newQuestion.brand.trim()) {
      payload.level = newQuestion.brand.trim();
    }
    if (user?.sub) {
      payload.user_id = user.sub;
    }
    if (user?.email) {
      payload.user_email = user.email;
    }

    const { data, error } = await insertIssue(payload);

    if (error) {
      console.error('[Clarifications] Submit error:', error);
      setQuestionError(`Could not submit question. Please try again. ${error.message || ''}`);
      setIsSavingQuestion(false);
      return;
    }

    if (data) {
      setQuestions(prev => [data, ...prev]);
    }

    setIsSavingQuestion(false);
    setShowNewQuestionModal(false);
    setNewQuestion({});
  };

  const toggleQuestion = (id) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Answered':
        return (
          <Badge variant="success" className="gap-1">
            <CheckCircle size={14} /> Answered
          </Badge>
        );
      case 'Closed':
        return (
          <Badge variant="default" className="gap-1">
            <XCircle size={14} /> Closed
          </Badge>
        );
      default:
        return (
          <Badge variant="warning" className="gap-1">
            <Clock size={14} /> Open
          </Badge>
        );
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const stats = useMemo(() => ({
    total: questions.length,
    my: questions.filter(q => q.userEmail === user?.email).length,
    open: questions.filter(q => (q.resolutionStatus || 'Open') === 'Open').length,
    answered: questions.filter(q => q.resolutionStatus === 'Answered').length,
  }), [questions, user]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Clarifications"
        description="Ask questions and browse answers from management"
        actions={
          <Button onClick={openNewQuestionModal} leftIcon={<Plus size={18} />}>
            Ask Question
          </Button>
        }
      />

      {/* Search and Stats Bar */}
      <Card className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1 max-w-2xl">
            <Input
              placeholder="Search questions, answers, retailers, categories..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              leadingIcon={<Search size={18} />}
            />
          </div>
          <div className="flex items-center gap-4 text-sm text-neutral-600">
            <span>Total: <strong className="text-neutral-900">{stats.total}</strong></span>
            <span>My: <strong className="text-primary-600">{stats.my}</strong></span>
            <span>Open: <strong className="text-warning-600">{stats.open}</strong></span>
            <span>Answered: <strong className="text-success-600">{stats.answered}</strong></span>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button
              variant={activeTab === 'all' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setActiveTab('all')}
            >
              All Questions
            </Button>
            <Button
              variant={activeTab === 'my' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setActiveTab('my')}
            >
              My Questions
            </Button>
          </div>

          <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showAnsweredOnly}
              onChange={(e) => setShowAnsweredOnly(e.target.checked)}
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            Answered Only
          </label>
        </div>
      </Card>

      {/* Questions List */}
      {isLoading ? (
        <Card className="flex items-center justify-center h-64">
          <Spinner size="lg" label="Loading clarifications..." />
        </Card>
      ) : apiError ? (
        <Card className="flex items-center justify-center h-64 bg-error-50 border-error-200">
          <p className="text-error-700 font-medium">Could not load clarifications from the API.</p>
        </Card>
      ) : filteredQuestions.length === 0 ? (
        <Card>
          <EmptyState
            icon={<MessageSquare />}
            title={searchInput ? 'No matches found' : activeTab === 'my' ? "You haven't asked any questions yet" : 'No clarifications found'}
            description={searchInput ? 'Try different keywords' : 'Ask a question to get started!'}
            action={
              !searchInput && (
                <Button onClick={openNewQuestionModal} leftIcon={<Plus size={16} />}>
                  Ask Your First Question
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredQuestions.map((question) => {
            const isExpanded = expandedQuestions.has(question.id);
            const hasAnswer = question.resolutionNotes && question.resolutionNotes.trim();
            const status = question.resolutionStatus || 'Open';
            const category = question.issueType || 'General';

            return (
              <Card key={question.id} hoverable className="p-5">
                {/* Status and Metadata */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {getStatusBadge(status)}
                    {question.retailer && (
                      <Badge variant="primary">{question.retailer}</Badge>
                    )}
                    {question.level && (
                      <Badge variant="primary">{question.level}</Badge>
                    )}
                    <Badge>{category}</Badge>
                  </div>
                  <span className="text-xs text-neutral-500">
                    {formatRelativeTime(question.createdAt)}
                  </span>
                </div>

                {/* Question Text */}
                <p className="text-neutral-800 text-base leading-relaxed mb-3">
                  {question.comment}
                </p>

                {/* Asked By */}
                <div className="flex items-center justify-between text-xs text-neutral-500 mb-3">
                  <span>Asked by: <strong className="text-neutral-700">{question.userEmail || 'Unknown'}</strong></span>
                  {hasAnswer && (
                    <button
                      onClick={() => toggleQuestion(question.id)}
                      className="flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium"
                    >
                      {isExpanded ? 'Hide Answer' : 'Show Answer'}
                      <ChevronDown
                        size={14}
                        className={cn('transform transition-transform', isExpanded && 'rotate-180')}
                      />
                    </button>
                  )}
                </div>

                {/* Answer Section */}
                {hasAnswer && isExpanded && (
                  <div className="mt-4 pt-4 border-t border-neutral-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="text-success-600" size={16} />
                      <span className="text-sm font-semibold text-neutral-700">Management Answer:</span>
                    </div>
                    <p className="text-neutral-700 text-sm leading-relaxed bg-success-50 p-3 rounded-lg border border-success-200">
                      {question.resolutionNotes}
                    </p>
                    <p className="text-xs text-neutral-500 mt-2">
                      Answered on: {question.updatedAt ? new Date(question.updatedAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                )}

                {/* No Answer Yet */}
                {!hasAnswer && status === 'Open' && (
                  <div className="mt-3 pt-3 border-t border-neutral-200">
                    <p className="text-sm text-neutral-500 italic">
                      Waiting for management response...
                    </p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* New Question Modal */}
      <Modal
        open={showNewQuestionModal}
        onClose={() => {
          if (!isSavingQuestion) {
            setShowNewQuestionModal(false);
            setNewQuestion({});
          }
        }}
        title="Ask a Question"
        size="lg"
      >
        {questionError && (
          <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg">
            <p className="text-sm font-medium text-error-700">{questionError}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-neutral-500 uppercase mb-1">
              Your Question <span className="text-error-500">*</span>
            </label>
            <textarea
              value={newQuestion.text || ''}
              onChange={(e) => handleNewQuestionChange('text', e.target.value)}
              rows={4}
              placeholder="Type your question here..."
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-500 uppercase mb-2">
              Category <span className="text-error-500">*</span>
            </label>
            <div className="flex flex-wrap gap-3">
              {QUESTION_CATEGORIES.map((cat) => (
                <label key={cat} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="category"
                    value={cat}
                    checked={newQuestion.category === cat}
                    onChange={(e) => handleNewQuestionChange('category', e.target.value)}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">{cat}</span>
                </label>
              ))}
            </div>
          </div>

          <Input
            label="Retailer (Optional)"
            value={newQuestion.retailer || ''}
            onChange={(e) => handleNewQuestionChange('retailer', e.target.value)}
            placeholder="e.g., Walmart, Costco..."
          />

          <Input
            label="Brand/Category (Optional)"
            value={newQuestion.brand || ''}
            onChange={(e) => handleNewQuestionChange('brand', e.target.value)}
            placeholder="e.g., Coca-Cola, Frozen Foods..."
          />
        </div>

        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => {
              if (!isSavingQuestion) {
                setShowNewQuestionModal(false);
                setNewQuestion({});
              }
            }}
            disabled={isSavingQuestion}
          >
            Cancel
          </Button>
          <Button
            onClick={saveNewQuestion}
            loading={isSavingQuestion}
            leftIcon={<Save size={16} />}
          >
            Submit Question
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default Clarifications;
